import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { Badge } from "@/core/components/ui/badge";
import { Card, CardContent } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { usePayrollRosterVersion } from "@/payroll/roster/query";
import { RosterReadOnlyView } from "@/payroll/roster/roster-read-only-view";
import { RosterTableSkeleton } from "@/payroll/roster/roster-table-skeleton";
import { PAYROLL_SETTINGS_ROSTER_PATH } from "@/payroll/settings/routes";
import { currentPayrollResult } from "@/payroll/teams/query-result";
import { PayBreakdownPaneShell } from "./pay-breakdown-pane-shell";

interface PayBreakdownRosterTabProps {
  businessId: string;
  // The roster version this period's shown run ran against (null for an open
  // period, or a run that predates roster snapshots).
  runVersionNumber: number | null;
  // The business's live published roster version.
  currentVersionNumber: number | null;
}

// Read-only view of the roster a period's run actually used. The run links to an
// immutable version snapshot; editing always happens in the dedicated roster
// editor against the LIVE roster (affecting future runs), never here.
export const PayBreakdownRosterTab = ({
  businessId,
  runVersionNumber,
  currentVersionNumber,
}: PayBreakdownRosterTabProps) => {
  // The version this period is anchored to: the run's version, or — with no
  // completed run yet — the live published version (what the next run will use).
  // This is a read-only view; there is no in-place version switch (edit the live
  // roster via the editor link).
  const version = runVersionNumber ?? currentVersionNumber;
  const query = usePayrollRosterVersion(businessId, version);
  const { data: roster, isLoading, isError } = currentPayrollResult(query);

  // Currency of the shown version relative to the live published roster:
  // `Current` when it matches, `Older version` when the run's snapshot is behind.
  // Informational tags only — no toggle.
  const displayedVersion = roster?.version_number ?? null;
  const displayedIsCurrent =
    displayedVersion != null &&
    currentVersionNumber != null &&
    displayedVersion === currentVersionNumber;
  const displayedIsOlder =
    displayedVersion != null &&
    currentVersionNumber != null &&
    displayedVersion < currentVersionNumber;

  if (version == null) {
    return (
      <PayBreakdownPaneShell fillHeight>
        <Card className="rounded-lg border-stroke-tertiary bg-surface-primary shadow-none">
          <CardContent className="p-8">
            <Stack gap="xs" align="center" className="text-center">
              <Typography variant="subtitleW1">
                No payroll roster configured
              </Typography>
              <Typography textColor="secondary">
                A roster hasn't been published for this business yet.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </PayBreakdownPaneShell>
    );
  }

  return (
    <PayBreakdownPaneShell fillHeight className="pb-20">
      <Card className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border-stroke-tertiary bg-surface-primary shadow-none">
        {/* Header lives inside the pane, above the table — version context on the
            left, the Edit action on the right — so the whole roster reads as one
            unit instead of metadata floating above a separate card. */}
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap="md"
          fullWidth
          className="shrink-0 flex-wrap gap-y-2 border-b border-stroke-tertiary px-4 py-3"
        >
          <Stack gap="xs">
            <Stack
              direction="row"
              align="center"
              gap="sm"
              className="flex-wrap"
            >
              {/* The active tab already says "Roster", so the title is dropped —
                  just the version badges remain as slim context. */}
              {roster?.version_number != null && (
                <Badge variant="gray" size="sm">
                  v{roster.version_number}
                </Badge>
              )}
              {runVersionNumber != null && (
                <Badge variant="green" size="sm">
                  Used by most recent run
                </Badge>
              )}
              {/* Current-vs-older signal, relative to the live published roster. */}
              {displayedIsCurrent && (
                <Badge variant="blue" size="sm">
                  Current
                </Badge>
              )}
              {displayedIsOlder && (
                <Badge variant="gold" size="sm">
                  Older version
                </Badge>
              )}
            </Stack>
            <Typography variant="bodyW3" textColor="secondary">
              {runVersionNumber != null
                ? "The roster this period's payroll ran against."
                : "The current published roster — what the next run will use."}
              {roster?.published_at
                ? ` · Published ${dayjs(roster.published_at).format("MMM D, YYYY")}`
                : ""}
            </Typography>
          </Stack>

          <Link
            to={PAYROLL_SETTINGS_ROSTER_PATH}
            className="inline-flex shrink-0 items-center gap-1 text-md font-medium text-interactive-text transition-colors hover:text-primary-text"
          >
            Open current roster
            <ArrowRightIcon className="size-4" />
          </Link>
        </Stack>

        <CardContent className="flex min-h-0 flex-1 flex-col p-0">
          {isLoading ? (
            <RosterTableSkeleton />
          ) : isError ? (
            <Stack
              align="center"
              justify="center"
              className="min-h-0 w-full flex-1 px-6 text-center"
            >
              <Typography textColor="secondary">
                This roster version could not be loaded.
              </Typography>
            </Stack>
          ) : roster ? (
            <RosterReadOnlyView
              key={`${businessId}-${version}`}
              businessId={businessId}
              roster={roster}
            />
          ) : (
            <Stack
              align="center"
              justify="center"
              className="min-h-0 w-full flex-1 px-6 text-center"
            >
              <Typography textColor="secondary">
                This roster version is no longer available.
              </Typography>
            </Stack>
          )}
        </CardContent>
      </Card>
    </PayBreakdownPaneShell>
  );
};
